export interface FeatureDetail {
  readonly availability: 'Free' | 'Paid plan';
  readonly description: string;
  readonly details: readonly string[];
  readonly id: string;
  readonly summary: string;
  readonly title: string;
}

export const features: readonly FeatureDetail[] = [
  {
    availability: 'Paid plan',
    description: 'Start with a reference image and describe how the scene should move. Seedance 2.5 brings image-to-video generation into a workflow built around your creative direction.',
    details: ['Direct the action, atmosphere, and camera movement in your prompt.', 'Choose supported duration and resolution settings and use the model’s audio generation capabilities.', 'Generation requires a paid plan. Review upgrades in your account after your first month.'],
    id: 'seedance',
    summary: 'Turn an image into video with motion and sound.',
    title: 'Seedance 2.5'
  },
  {
    availability: 'Paid plan',
    description: 'Bring references, research, and visual ideas into your story with Gemini. Analyze footage, develop original scenarios, and create or refine images in the project’s creative assistant.',
    details: ['Analyze reference video and audio for scene summaries, key moments, camera direction, and pacing notes.', 'Turn a creative brief into scenario drafts and shot prompts, with Google Search sources for research-backed ideas.', 'Generate and edit storyboard frames and reference images through an iterative creative conversation.', 'Choose Gemini in the creative assistant and review the credit estimate before submitting. Provider access must be configured on the server.'],
    id: 'gemini',
    summary: 'Understand footage, develop scenarios, and shape images.',
    title: 'Gemini creative tools'
  },
  {
    availability: 'Free',
    description: 'Elements keep the people and products at the heart of a campaign reusable. Build lifelike virtual models and plan product placements for ads, influencer content, and social series.',
    details: ['Reuse model references across new outfits, locations, and campaign concepts.', 'Plan product reveals, everyday routines, and scenes that put your product in context.', 'Save reusable Elements, assign them to shots, and describe product placement. Selected references inform Gemini image requests; visual consistency depends on the model.'],
    id: 'elements',
    summary: 'Reusable models and products for campaigns.',
    title: 'Elements & placements'
  },
  {
    availability: 'Free',
    description: 'Create a cast for each project and keep the details that define every character together. Start with their name, appearance, personality, and role in the story.',
    details: ['Save character descriptions and optional reference links in your project.', 'Assign characters to shots so their descriptions can become part of the generation prompt during generation.', 'Build a shared foundation for consistency. Generated visual consistency depends on the model and references; it is not guaranteed.'],
    id: 'characters',
    summary: 'Keep character descriptions and references together.',
    title: 'Characters & identity'
  },
  {
    availability: 'Free',
    description: 'Character profiles bring memory, emotion, and relationships into story continuity. What happens to a character should help shape what they do next.',
    details: ['Carry meaningful experiences, knowledge, and unresolved moments across scenes.', 'Develop trust, tension, confidence, and other emotional changes through choices and consequences.', 'Add backstory or record a memory after a specific shot. Subsequent shot prompts include that history without exposing future events.'],
    id: 'memory',
    summary: 'Carry experiences and emotions across scenes.',
    title: 'Memory & emotions'
  },
  {
    availability: 'Free',
    description: 'Give your idea a home before you generate a single frame. A project brings your story brief, visual direction, cast, and shot list into one place.',
    details: ['Create and edit a named project with a story brief and visual direction.', 'Save your work to your private account and return to continue developing it.', 'Keep your brief, cast, and shots together. Creator and Studio include a JSON planning export; generation and media storage require a paid plan.'],
    id: 'projects',
    summary: 'Save your brief, cast, and story in one place.',
    title: 'Projects & story planning'
  },
  {
    availability: 'Free',
    description: 'Break your story into individual shots. Give each moment its own description, timing, references, and cast while keeping it connected to the project.',
    details: ['Write and edit shot titles and descriptions, and plan duration and resolution.', 'Associate character descriptions and image reference links with the shots that need them.', 'Save the storyboard before upgrading. Writing a shot does not trigger generation or reserve credits.'],
    id: 'storyboards',
    summary: 'Write shots, set timing, and assign characters.',
    title: 'Storyboards & shot direction'
  },
  {
    availability: 'Free',
    description: 'Set a common visual direction for your story so every shot starts from the same creative brief.',
    details: ['Describe your palette, lighting, atmosphere, and camera style at the project level.', 'Add scene-specific direction without rewriting your project’s overall look.', 'Project style and story context feed into shot prompts during generation. Planning the look is included in Free.'],
    id: 'style',
    summary: 'Define your project’s look and atmosphere.',
    title: 'Visual direction'
  },
  {
    availability: 'Paid plan',
    description: 'The quick-video workflow is designed for a standalone idea that does not need a full storyboard: a product moment, an atmospheric clip, or a social concept.',
    details: ['Describe the clip and supply a reference image where the model requires one.', 'Keep standalone videos separate from your story projects.', 'Free accounts can plan stories, but cannot create quick-video jobs or store videos. Review upgrades in your account after your first month.'],
    id: 'quick-video',
    summary: 'Create a standalone clip without a storyboard.',
    title: 'Quick videos'
  },
  {
    availability: 'Paid plan',
    description: 'The generation workflow keeps results associated with the shot that produced them, so you can review attempts and choose the direction that works.',
    details: ['Review a shot’s takes and mark the preferred result as approved.', 'Keep generated assets connected to their project and shot.', 'The media library and generated downloads depend on generation and storage access. Free accounts have no generated assets or video storage.'],
    id: 'takes',
    summary: 'Review, approve, and organize generated media.',
    title: 'Takes & asset library'
  },
  {
    availability: 'Free',
    description: 'Your account brings your details and credit balance together. Free membership gives you room to plan without starting a generation job.',
    details: ['Free accounts start with zero credits and can create projects, characters, and story shots.', 'Generation estimates and reserved/spent balances are part of the generation workflow; free accounts cannot submit those jobs.', 'Paid plan allowances are provisional. After your first month, use your account to review checkout options and manage your subscription.'],
    id: 'credits',
    summary: 'View your membership and credit balance.',
    title: 'Account, credits & upgrades'
  }
];
